import React, { useState, useEffect, useCallback } from 'react';
// Corrected: Importing doc, getDoc, and db from the centralized firebase.js
import { db, doc, getDoc } from '../firebase';
import Modal from './Modal';

/**
 * NPSModal Component
 * Fetches the active campaign and allows the user to submit a score (0-10) and an optional comment.
 * Automatically dismisses if the current date is past the campaign's set endDate.
 */
export default function NPSModal({ campaignId, onSubmit, onDismiss }) {
    const [campaign, setCampaign] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [score, setScore] = useState(null);
    const [comment, setComment] = useState('');
    const [isExpired, setIsExpired] = useState(false);

    // useCallback is used to stabilize the onDismiss function dependency
    const fetchCampaign = useCallback(async () => {
        if (!campaignId || typeof campaignId !== 'string') {
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        const cleanedCampaignId = campaignId.trim();

        try {
            const campaignRef = doc(db, 'nps_campaigns', cleanedCampaignId);
            const docSnap = await getDoc(campaignRef);

            if (docSnap.exists()) {
                const campaignData = docSnap.data();

                // 1. Check if the campaign has an end date and if it's in the past
                if (campaignData.endDate) {
                    const endTime = campaignData.endDate.toMillis();
                    const now = Date.now();
                    
                    if (now >= endTime) {
                        console.log("NPS campaign is expired.");
                        setIsExpired(true);
                        onDismiss(); // Immediately dismiss the modal if expired
                        return;
                    }
                }
                
                // 2. Check if the campaign has a start date in the future
                if (campaignData.startDate) {
                    const startTime = campaignData.startDate.toMillis();
                    const now = Date.now();
                    if (now < startTime) {
                        console.log("NPS campaign has not started yet.");
                        onDismiss(); 
                        return;
                    }
                }

                setCampaign(campaignData);

            } else {
                console.error("Error: NPS campaign document not found for ID:", cleanedCampaignId);
            }
        } catch (error) {
            console.error("Error fetching NPS campaign:", error);
        } finally {
            setIsLoading(false);
        }
    }, [campaignId, onDismiss]);

    useEffect(() => {
        fetchCampaign();
    }, [fetchCampaign]);

    const handleSubmit = () => {
        if (score === null) {
            console.error("Por favor, selecione uma nota de 0 a 10.");
            return;
        }
        if (onSubmit) {
            // Pass score, campaignId, and the optional comment
            onSubmit(score, campaignId, comment.trim());
            setScore(null); // Reset state after submission
            setComment('');
        }
    };

    const NPSButton = ({ number }) => {
        const isSelected = score === number;
        let bgColor = 'bg-white';
        let textColor = 'text-gray-700';
        
        // Define colors based on score type
        if (isSelected) {
            if (number <= 6) { bgColor = 'bg-red-500'; textColor = 'text-white'; }
            else if (number <= 8) { bgColor = 'bg-yellow-400'; textColor = 'text-white'; }
            else { bgColor = 'bg-green-500'; textColor = 'text-white'; }
        }

        return (
            <button
                onClick={() => setScore(number)}
                className={`w-10 h-10 flex items-center justify-center font-bold border rounded-full transition-all duration-200 shadow-md 
                    ${isSelected ? `${bgColor} ${textColor} border-transparent scale-110` : 'border-gray-300 hover:bg-gray-100 hover:scale-[1.05]'}
                `}
                aria-label={`Score ${number}`}
            >
                {number}
            </button>
        );
    };

    if (isLoading || isExpired || !campaign) {
        return null; 
    }

    return (
        <Modal isOpen={true} onClose={onDismiss} title="Sua opinião é importante!">
            <div className="text-center">
                <p className="text-gray-600 mb-6 font-medium">{campaign.question}</p>
                
                {/* Score Selection Buttons */}
                <div className="flex flex-wrap justify-center gap-2 mb-6">
                    {Array.from({ length: 11 }, (_, i) => i).map(number => (
                        <NPSButton key={number} number={number} />
                    ))}
                </div>

                <div className="flex justify-between items-center text-xs text-gray-500 font-medium px-1 mb-6">
                    <span>0 - Detrator (Pouco Provável)</span>
                    <span>10 - Promotor (Muito Provável)</span>
                </div>

                {/* Optional Comment Field - Only show after score selection */}
                {score !== null && (
                    <div className="mt-4">
                        <textarea
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                            placeholder="Compartilhe seu comentário (opcional)..."
                            rows="3"
                            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-green-500 resize-none shadow-inner"
                        />
                    </div>
                )}
                
                {/* Action Buttons */}
                <div className="mt-8 flex justify-end space-x-4">
                    <button
                        type="button"
                        onClick={onDismiss}
                        className="px-6 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition duration-150"
                    >
                        Agora não
                    </button>
                    <button
                        type="button"
                        onClick={handleSubmit}
                        disabled={score === null}
                        className="px-6 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-lg hover:bg-green-700 transition duration-150 disabled:bg-gray-400 disabled:cursor-not-allowed"
                    >
                        Enviar Resposta
                    </button>
                </div>
            </div>
        </Modal>
    );
}
